import * as styles from "./ReplacementList.css";

import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, type ReactNode, useRef, useState } from "react";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { List, ListEmpty, ListItem } from "@frontend/components/atoms/List";
import { Skeleton } from "@frontend/components/atoms/Skeleton";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { EmployeeAvatar } from "@frontend/shared/employee-field";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  assignAllAtom,
  patchReplacementAtom,
  selectedDraftAtom,
  selectedSlotsAtom,
  selectedSlotsLoadingAtom,
} from "../stores";
import type {
  ReplacementDraft,
  ReplacementKind,
  ReplacementSlot,
} from "../types";
import { coveredCount, slotDecision } from "../utils/draft";
import { ReplacementDialog } from "./ReplacementDialog";

const SKELETON_ROWS = 5;

const KIND_ICONS: Record<Exclude<ReplacementKind, "employee">, IconName> = {
  none: "UserX",
  self: "UserCheck",
  unassigned: "UserPlus",
};

type Edited = { replacedJobId: number } | "all" | null;

function formatShort(value: string | null, locale: string): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  return Number.isNaN(date.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
      }).format(date);
}

function ReplacementRowsSkeleton(): JSX.Element {
  return (
    <List className={styles.list} separators aria-busy>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <ListItem
          key={index}
          startSection={
            <Skeleton radius="full" className={styles.skeletonAvatar} />
          }
        >
          <Skeleton className={styles.skeletonJob} />
          <Skeleton className={styles.skeletonSummary} />
        </ListItem>
      ))}
    </List>
  );
}

function ReplacementRow({
  slot,
  decision,
  dirty,
  onOpen,
}: {
  slot: ReplacementSlot;
  decision: ReplacementDraft;
  dirty: boolean;
  onOpen: (origin: HTMLElement) => void;
}): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const begin = formatShort(slot.jobBeginDate, locale);
  const end = formatShort(slot.jobEndDate, locale);

  let who: ReactNode;

  if ("employee" === decision.kind && decision.employee) {
    who = <span className={styles.who}>{decision.employee.label}</span>;
  } else if ("none" === decision.kind) {
    who = (
      <span className={styles.nobody}>
        <Trans>No replacement</Trans>
      </span>
    );
  } else {
    who = null;
  }

  const reason = decision.justification.trim();

  return (
    <ListItem
      value={String(slot.replacedJobId)}
      onClick={(event) => onOpen(event.currentTarget)}
      startSection={
        "employee" === decision.kind && decision.employee ? (
          <EmployeeAvatar
            employee={{
              id: decision.employee.id,
              label: decision.employee.label,
              department: null,
              available: true,
              avatarUrl: decision.employee.avatarUrl,
              hasPhoto: decision.employee.hasPhoto,
            }}
          />
        ) : (
          <Avatar
            size="sm"
            className={styles.mark}
            fallback={
              <Icon
                name={
                  "employee" === decision.kind
                    ? "UserRound"
                    : KIND_ICONS[decision.kind]
                }
              />
            }
          />
        )
      }
      endSection={
        <span className={styles.end}>
          {dirty ? (
            <span className={styles.dirty} aria-label={t`Unsaved changes`} />
          ) : null}

          {begin && end ? (
            <span className={styles.period}>
              {begin} → {end}
            </span>
          ) : null}
        </span>
      }
    >
      <span className={styles.body}>
        <span className={styles.head}>
          <span className={styles.job} title={slot.projectName ?? undefined}>
            {slot.projectName}
          </span>

          {"unassigned" === decision.kind ? (
            <Badge variant="warning" size="sm">
              <Trans>To be assigned</Trans>
            </Badge>
          ) : null}

          {"self" === decision.kind ? (
            <Badge variant="neutral" size="sm">
              <Trans>Keeps the job</Trans>
            </Badge>
          ) : null}
        </span>

        {who || reason ? (
          <span className={styles.summary}>
            {who}
            {who && reason ? " · " : null}
            {reason}
          </span>
        ) : null}
      </span>
    </ListItem>
  );
}

export function ReplacementList(): JSX.Element {
  const { t } = useLingui();

  const draft = useAtomValue(selectedDraftAtom);
  const slots = useAtomValue(selectedSlotsAtom);
  const loading = useAtomValue(selectedSlotsLoadingAtom);

  const patchReplacement = useSetAtom(patchReplacementAtom);
  const assignAll = useSetAtom(assignAllAtom);

  const [edited, setEdited] = useState<Edited>(null);

  // The row the popup grows from.
  const originRef = useRef<DOMRect | null>(null);

  const covered = coveredCount(slots, draft);

  const editedSlot =
    null === edited || "all" === edited
      ? null
      : (slots.find((slot) => slot.replacedJobId === edited.replacedJobId) ??
        null);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h3 className={styles.title}>
            <Trans>Replacements</Trans>
          </h3>

          {slots.length > 0 && (
            <span className={styles.coverage}>
              <Trans>
                {covered} / {slots.length} jobs covered
              </Trans>
            </span>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          disabled={0 === slots.length}
          leftSection={<Icon name="Users" size="sm" />}
          onClick={(event) => {
            originRef.current = event.currentTarget.getBoundingClientRect();
            setEdited("all");
          }}
        >
          {t`Assign to all…`}
        </Button>
      </div>

      {loading && 0 === slots.length ? (
        <ReplacementRowsSkeleton />
      ) : (
        <List className={styles.list} fade separators label={t`Replacements`}>
          {0 === slots.length ? (
            <ListEmpty className={styles.empty}>
              <Trans>No job to cover over this period.</Trans>
            </ListEmpty>
          ) : (
            slots.map((slot) => (
              <ReplacementRow
                key={slot.replacedJobId}
                slot={slot}
                decision={slotDecision(slot, draft)}
                dirty={undefined !== draft?.replacements[slot.replacedJobId]}
                onOpen={(origin) => {
                  originRef.current = origin.getBoundingClientRect();
                  setEdited({ replacedJobId: slot.replacedJobId });
                }}
              />
            ))
          )}
        </List>
      )}

      {"all" === edited ? (
        <ReplacementDialog
          slot={null}
          decision={{
            kind: "none",
            employeeId: null,
            employee: null,
            justification: "",
          }}
          from={draft?.beginDate ?? null}
          to={draft?.endDate ?? null}
          originRef={originRef}
          onApply={(decision) => {
            assignAll(decision);
            setEdited(null);
          }}
          onClose={() => setEdited(null)}
        />
      ) : editedSlot ? (
        <ReplacementDialog
          key={editedSlot.replacedJobId}
          slot={editedSlot}
          decision={slotDecision(editedSlot, draft)}
          from={draft?.beginDate ?? null}
          to={draft?.endDate ?? null}
          originRef={originRef}
          onApply={(decision) => {
            patchReplacement(editedSlot.replacedJobId, decision);
            setEdited(null);
          }}
          onClose={() => setEdited(null)}
        />
      ) : null}
    </>
  );
}
